import { useEffect, useMemo, useState } from 'react';

import { useStoreContext } from 'features/store';
import { getStoreLocalTime } from 'features/store/utils/store-time-utils';

import {
  LoyaltyPromotionType,
  LoyaltyServiceMode,
  useGetLoyaltyOffersLazyQuery,
  useLoyaltyUserOffersLazyQuery,
} from 'generated/graphql-gateway';
import { useLoyaltyOfferGroupsByIdsLazyQuery } from 'generated/sanity-graphql';
import { useCart } from 'hooks/use-cart';
import { useEffectEvent } from 'hooks/use-effect-event';
import { useLoyaltyIncentivesAvailabilityUtils } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { useServiceModeContext } from 'state/service-mode';
import logger from 'utils/logger';
import { blockContentToPlainText } from 'utils/sanity';

import {
  AppSystemwideOffer,
  EngineSystemwideOffer,
  SanityId,
  SanitySystemwideOffer,
  buildAppSystemwideOffer,
} from '../types';

import { IIncentiveEvaluationResult, IncentiveEvaluationErrorCodes } from './types';
import { useLoyaltyUserId } from './use-loyalty-user';
import {
  createFeedbackMaps,
  getEvaluationResultMessage,
  pushUnavailableOffersToBottom,
} from './utils/helpers';

const OFFER_NOT_AVAILABLE_EVALUATION_RESULT: IIncentiveEvaluationResult = {
  code: IncentiveEvaluationErrorCodes.OFFER_NOT_AVAILABLE,
  currentValue: null,
  message: '',
  ruleId: '',
  targetValue: null,
};

function useGetCmsOffersByGroupId(groupId?: SanityId) {
  const { store } = useStoreContext();
  const storeId = store?.number;
  const { loyaltyUserId } = useLoyaltyUserId();

  const [getOffers, { data, loading }] = useLoyaltyOfferGroupsByIdsLazyQuery({
    nextFetchPolicy: 'cache-and-network',
  });

  const onCmsOffersCompleted = useEffectEvent((completedData: typeof data) => {
    if (!completedData?.allOfferGroups?.[0]?.offers?.length) {
      logger.warn({ groupId, storeId, loyaltyId: loyaltyUserId, msg: 'offer_group_cms_empty' });
    }
  });

  useEffect(() => {
    if (!groupId || data || loading) {
      return;
    }
    getOffers({
      variables: {
        ids: groupId,
      },
      onCompleted: onCmsOffersCompleted,
    });
    // onCmsOffersCompleted is a stable reference from useEffectEvent — intentionally excluded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, getOffers, data, loading]);

  const offers = data?.allOfferGroups?.[0]?.offers as SanitySystemwideOffer[];
  const groupName = blockContentToPlainText(data?.allOfferGroups?.[0]?.name?.localeRaw);
  const canOnlyChooseOne = data?.allOfferGroups?.[0]?.canOnlyChooseOne;
  return { offers, groupName, canOnlyChooseOne, loading };
}

/**
 * Fetches loyalty offers for a given set of CMS offers.
 *
 * This hook first attempts to fetch offers for a logged-in user using their loyalty ID.
 * If the user is not logged in, it fetches offers anonymously.
 * The fetched offers are filtered based on the service mode and store ID.
 *
 * @param sanityOffers - An array of CMS offers to fetch loyalty offers for.
 * @returns An object containing the fetched offers and a loading state.
 */
function useGetLoyaltyOffers(sanityOffers: SanitySystemwideOffer[] | undefined) {
  const { loyaltyUserId } = useLoyaltyUserId();

  const { serviceMode: ctxServiceMode } = useServiceModeContext();
  const serviceMode = Object.entries(LoyaltyServiceMode).find(
    ([key]) => key === ctxServiceMode
  )?.[1];

  const { store } = useStoreContext();
  const storeId = store?.number;

  const [
    getUserOffers,
    { data: userOffers, loading: loadingUserOffers },
  ] = useLoyaltyUserOffersLazyQuery();
  const [
    getAnonOffers,
    { data: anonOffers, loading: loadingAnonOffers },
  ] = useGetLoyaltyOffersLazyQuery();

  useEffect(() => {
    if (!sanityOffers?.length || !loyaltyUserId || userOffers || loadingUserOffers) {
      if (Array.isArray(sanityOffers) && !sanityOffers.length) {
        logger.warn({
          storeId,
          serviceMode,
          queryType: 'user',
          loyaltyId: loyaltyUserId,
          msg: 'offer_group_engine_skipped_no_cms_offers',
        });
      }
      return;
    }

    const cmsOffersEngineIds = sanityOffers?.map(o => o.loyaltyEngineId);
    logger.info({
      loyaltyId: loyaltyUserId,
      storeId,
      serviceMode,
      queryType: 'user',
      offerIds: cmsOffersEngineIds,
      userDateTime: getStoreLocalTime(),
      msg: 'offer_group_engine_called',
    });
    getUserOffers({
      fetchPolicy: 'network-only',
      variables: {
        loyaltyId: loyaltyUserId,
        where: {
          ids: cmsOffersEngineIds,
          serviceMode: serviceMode || undefined,
          storeId,
          omitInvalids: false,
        },
      },
    });
  }, [
    getUserOffers,
    sanityOffers,
    loyaltyUserId,
    serviceMode,
    storeId,
    userOffers,
    loadingUserOffers,
  ]);

  useEffect(() => {
    if (!sanityOffers?.length || anonOffers || loadingAnonOffers) {
      if (Array.isArray(sanityOffers) && !sanityOffers.length) {
        logger.warn({
          storeId,
          serviceMode,
          queryType: 'anon',
          ...(loyaltyUserId && { loyaltyId: loyaltyUserId }),
          msg: 'offer_group_engine_skipped_no_cms_offers',
        });
      }
      return;
    }
    const cmsOffersEngineIds = sanityOffers?.map(o => o.loyaltyEngineId);
    logger.info({
      storeId,
      serviceMode,
      queryType: 'anon',
      ...(loyaltyUserId && { loyaltyId: loyaltyUserId }),
      offerIds: cmsOffersEngineIds,
      userDateTime: getStoreLocalTime(),
      msg: 'offer_group_engine_called',
    });
    getAnonOffers({
      fetchPolicy: 'network-only',
      variables: {
        where: {
          ids: cmsOffersEngineIds,
          serviceMode: serviceMode || undefined,
          storeId,
          omitInvalids: false,
        },
      },
    });
  }, [
    getAnonOffers,
    sanityOffers,
    serviceMode,
    storeId,
    loyaltyUserId,
    anonOffers,
    loadingAnonOffers,
  ]);

  useEffect(() => {
    if (!loadingUserOffers && userOffers && !userOffers.loyaltyUserV2?.offers?.length) {
      logger.warn({
        loyaltyId: loyaltyUserId,
        storeId,
        serviceMode,
        queryType: 'user',
        msg: 'offer_group_engine_returned_empty',
      });
    }
  }, [userOffers, loadingUserOffers, loyaltyUserId, storeId, serviceMode]);

  useEffect(() => {
    if (!loadingAnonOffers && anonOffers && !anonOffers.loyaltyOffersV2?.length) {
      logger.warn({
        storeId,
        serviceMode,
        queryType: 'anon',
        ...(loyaltyUserId && { loyaltyId: loyaltyUserId }),
        msg: 'offer_group_engine_returned_empty',
      });
    }
  }, [anonOffers, loadingAnonOffers, storeId, serviceMode, loyaltyUserId]);

  const offers = (userOffers?.loyaltyUserV2?.offers ||
    anonOffers?.loyaltyOffersV2) as EngineSystemwideOffer[];
  return { offers, loading: loadingUserOffers || loadingAnonOffers };
}

type SanitySwOfferWithInlineAlertMessage = SanitySystemwideOffer & {
  inlineAlertMessage?: string;
};

function useAddUnavailabilityMessages(sanityOffers: SanitySystemwideOffer[] | undefined) {
  const { getIncentiveUnavailableMessage, ready } = useLoyaltyIncentivesAvailabilityUtils();
  const [offers, setOffers] = useState<SanitySwOfferWithInlineAlertMessage[] | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sanityOffers?.length || !ready || loading) {
      return;
    }
    setLoading(true);
    // Add unavailability messages to each offer
    const nextOffers = sanityOffers.map(offer => {
      const inlineAlertMessage = getIncentiveUnavailableMessage({ incentive: offer });
      return { ...offer, inlineAlertMessage };
    });

    setOffers(nextOffers);
    setLoading(false);
  }, [sanityOffers, getIncentiveUnavailableMessage, ready, loading]);

  return { offers, loading };
}

function useOfferEvaluation(
  sanityOffers: SanitySwOfferWithInlineAlertMessage[] | undefined,
  engineOffers: EngineSystemwideOffer[] | null | undefined
) {
  const [loading, setLoading] = useState(false);
  const { cartEntries } = useCart();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);

  const offers = useMemo(() => {
    if (!sanityOffers || !engineOffers || loading) {
      return [];
    }
    setLoading(true);
    const cmsOffersEngineIds = sanityOffers?.map(o => o.loyaltyEngineId);
    const { incentiveFeedbackMap, incentivesMapById } = createFeedbackMaps(engineOffers);

    // mapping no longer existing offers
    // if the offer didn't come back on the response then it doesn't exist
    cmsOffersEngineIds.forEach(id => {
      if (id && !incentivesMapById[id]) {
        incentiveFeedbackMap[id] = [OFFER_NOT_AVAILABLE_EVALUATION_RESULT];
      }
    });

    const filteredOffers: AppSystemwideOffer[] = [];
    for (const engineOffer of engineOffers) {
      const sanityOffer = sanityOffers.find(o => o.loyaltyEngineId === engineOffer.id);
      if (!sanityOffer) {
        continue;
      }
      const evaluationResult = incentiveFeedbackMap[sanityOffer.loyaltyEngineId || ''];
      const evaluationMessages = evaluationResult?.map(evaluationResult => {
        const { code, targetValue } = evaluationResult;
        if (code === IncentiveEvaluationErrorCodes.OUT_OF_DAY_PART) {
          const { incentive } = targetValue;
          const dayPart = incentive.incentives[0]?.operationalItem?.daypart[0];
          return `${dayPart} ${t('only')}`;
        } else if (code === IncentiveEvaluationErrorCodes.NOT_AVAILABLE_IN_STORE) {
          return t('rewardUnavailable');
        }
        // Fallback for other messaging
        const message = getEvaluationResultMessage({
          evaluationResult,
          incentiveType: LoyaltyPromotionType.OFFER,
          cartEntries,
        });
        return message as string;
      });

      // Prioritize the error message that is coming from the LoyaltyEngine
      const inlineAlertMessage = evaluationMessages
        ? evaluationMessages[0]
        : sanityOffer.inlineAlertMessage;

      const appOffer = buildAppSystemwideOffer(sanityOffer, engineOffer);
      appOffer.inlineAlertMessage = inlineAlertMessage;

      if (appliedOffers.find(applied => applied.id === appOffer.loyaltyEngineId)) {
        // Hide applied offers from the list
        continue;
      }

      filteredOffers.push(appOffer);
    }
    setLoading(false);
    return filteredOffers;
  }, [cartEntries, sanityOffers, loading, engineOffers, appliedOffers]);

  return { offers, loading };
}

export const useLoyaltyOfferGroupsById = (groupId?: SanityId) => {
  // Fetch the Offers from Sanity belonging to this group
  const {
    offers: sanitySystemideOffers,
    loading: sanityLoading,
    groupName,
    canOnlyChooseOne,
  } = useGetCmsOffersByGroupId(groupId);

  // After receiving the Offers from Sanity, fetch Offers from LoyaltyEngine
  // using Sanity's loyaltyEngineId
  const { offers: engineSystemwideOffers, loading: engineLoading } = useGetLoyaltyOffers(
    sanitySystemideOffers
  );

  // After receiving the Offers from Sanity, add unavailability reasons to each
  // of them
  const {
    offers: sanityOfferWithUnavailability,
    loading: unavailabilityLoading,
  } = useAddUnavailabilityMessages(sanitySystemideOffers);

  // As a final step, filter out Sanity Offers that where not returned by
  // LoyaltyEngine and add unavailability messages coming from LoyaltyEngine
  const { offers: evaluatedOffers, loading: evaluationLoading } = useOfferEvaluation(
    sanityOfferWithUnavailability,
    engineSystemwideOffers
  );

  // Move unavailable Offers to the bottom of the list
  const sortedOffers = useMemo(() => {
    if (Array.isArray(evaluatedOffers)) {
      return evaluatedOffers.sort(pushUnavailableOffersToBottom);
    }
    return [];
  }, [evaluatedOffers]);

  return {
    loading: sanityLoading || engineLoading || unavailabilityLoading || evaluationLoading,
    offers: sortedOffers,
    groupName,
    canOnlyChooseOne,
    sanitySystemideOffers,
  };
};
